import type { Attribute, AttributeType, EditingLayer } from "fond/types";

export interface AttributeField {
  ID: string;
  Type: AttributeType;
  Options?: string[];
  DependsOn?: string;
  OptionsMap?: Record<string, string[]>;
  DefaultValue?: string;
}

/**
 * Merges EditingLayer.Attributes with layer schema attributes, preserving order.
 * EditingLayer attributes override layer schema attributes when both exist.
 *
 * @param layerAttributes - Attributes from the layer schema
 * @param editingLayer - The EditingLayer configuration containing potential overrides
 * @returns Array of merged attributes
 */
export function mergeLayerAttributes(layerAttributes: Attribute[] | undefined, editingLayer: EditingLayer | undefined): AttributeField[] {
  const attributesArray: AttributeField[] = [];

  if (layerAttributes && layerAttributes.length > 0) {
    // Start with layer attributes to preserve order
    return layerAttributes.map((attribute) => {
      const attributeId = attribute.Name || attribute.ID;

      // Check if this attribute has an override in EditingLayer.Attributes
      const editingLayerOverride = editingLayer?.Attributes?.[attributeId];

      if (editingLayerOverride) {
        // Use the override from EditingLayer
        return {
          ID: attributeId,
          Type: editingLayerOverride.Type,
          Options: "Options" in editingLayerOverride ? editingLayerOverride.Options : undefined,
          DependsOn: "DependsOn" in editingLayerOverride ? editingLayerOverride.DependsOn : undefined,
          OptionsMap: "OptionsMap" in editingLayerOverride ? editingLayerOverride.OptionsMap : undefined,
          DefaultValue: "DefaultValue" in editingLayerOverride ? editingLayerOverride.DefaultValue : undefined,
        };
      }

      // Use the attribute from layer schema
      let attributeType: AttributeType = "STRING";
      if (attribute.Type === "INTEGER") {
        attributeType = "INTEGER";
      } else if (attribute.Type === "REAL") {
        attributeType = "REAL";
      }

      return {
        ID: attributeId,
        Type: attributeType,
      };
    });
  } else if (editingLayer?.Attributes && Object.keys(editingLayer.Attributes).length > 0) {
    // If there are no layer attributes, use only EditingLayer attributes
    return Object.entries(editingLayer.Attributes).map(([id, attributeConfig]) => ({
      ID: id,
      Type: attributeConfig.Type,
      Options: "Options" in attributeConfig ? attributeConfig.Options : undefined,
      DependsOn: "DependsOn" in attributeConfig ? attributeConfig.DependsOn : undefined,
      OptionsMap: "OptionsMap" in attributeConfig ? attributeConfig.OptionsMap : undefined,
      DefaultValue: "DefaultValue" in attributeConfig ? attributeConfig.DefaultValue : undefined,
    }));
  }

  return attributesArray;
}
